/**
 * Copyright 2022, 2024 Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { REQUEST_TIMEOUT_MS } from '../enums';
import { REQUEST_ERROR, REQUEST_TIMEOUT, UNABLE_TO_PARSE_AND_SKIPPED_HEADER } from 'error_message';
import { OptimizelyError } from '../../error/optimizly_error';
/**
 * Handles sending requests and receiving responses over HTTP via XMLHttpRequest
 */
export class BrowserRequestHandler {
    constructor(opt = {}) {
        var _a;
        this.logger = opt.logger;
        this.timeout = (_a = opt.timeout) !== null && _a !== void 0 ? _a : REQUEST_TIMEOUT_MS;
    }
    /**
     * Builds an XMLHttpRequest
     * @param requestUrl Fully-qualified URL to which to send the request
     * @param headers List of headers to include in the request
     * @param method HTTP method to use
     * @param data?? stringified version of data to POST, PUT, etc
     * @returns AbortableRequest contains both the response Promise and capability to abort()
     */
    makeRequest(requestUrl, headers, method, data) {
        const request = new XMLHttpRequest();
        const responsePromise = new Promise((resolve, reject) => {
            request.open(method, requestUrl, true);
            this.setHeadersInXhr(headers, request);
            request.onreadystatechange = () => {
                if (request.readyState === XMLHttpRequest.DONE) {
                    const statusCode = request.status;
                    if (statusCode === 0) {
                        reject(new OptimizelyError(REQUEST_ERROR));
                        return;
                    }
                    const headers = this.parseHeadersFromXhr(request);
                    const response = {
                        statusCode: request.status,
                        body: request.responseText,
                        headers,
                    };
                    resolve(response);
                }
            };
            request.timeout = this.timeout;
            request.ontimeout = () => {
                var _a;
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.warn(REQUEST_TIMEOUT);
            };
            request.send(data);
        });
        return {
            responsePromise,
            abort() {
                request.abort();
            },
        };
    }
    /**
     * Sets the header collection for an XHR
     * @param headers Headers to set
     * @param request Request into which headers are to be set
     * @private
     */
    setHeadersInXhr(headers, request) {
        Object.keys(headers).forEach(headerName => {
            const header = headers[headerName];
            if (typeof header === 'string') {
                request.setRequestHeader(headerName, header);
            }
        });
    }
    /**
     * Parses headers from an XHR
     * @param request Request containing headers to be retrieved
     * @private
     * @returns List of headers without duplicates
     */
    parseHeadersFromXhr(request) {
        const allHeadersString = request.getAllResponseHeaders();
        if (allHeadersString === null) {
            return {};
        }
        const headerLines = allHeadersString.split('\r\n');
        const headers = {};
        headerLines.forEach(headerLine => {
            var _a;
            try {
                const separatorIndex = headerLine.indexOf(': ');
                if (separatorIndex > -1) {
                    const headerName = headerLine.slice(0, separatorIndex);
                    const headerValue = headerLine.slice(separatorIndex + 2);
                    if (headerName && headerValue) {
                        headers[headerName] = headerValue;
                    }
                }
            }
            catch (_b) {
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.warn(UNABLE_TO_PARSE_AND_SKIPPED_HEADER, headerLine);
            }
        });
        return headers;
    }
}
export const __platforms = ['browser', 'react_native'];
//# sourceMappingURL=request_handler.browser.js.map